import { Component, Input, Output, EventEmitter, ChangeDetectionStrategy } from '@angular/core';
import { Column } from '@miq/catalog';

export interface SelectionTableOptions {
  selectMode?: 'single' | 'multi';
  dataKey?: string;
  tableTitle?: string;
  noun?: string;
}

@Component({
  selector: 'miq-selection-table',
  template: ` <!-- table toolbar -->
    <div class="table-container">
      <div class="table-header">
        <!-- title -->
        <div *ngIf="title != null" class="table-title">{{ title | xTranslate }}</div>

        <!-- noun count -->
        <div *ngIf="elements?.length > 0 && noun != null" class="data-results-num">
          {{ elements.length }} <span>{{ noun | xTranslate }}</span>
        </div>
      </div>
    </div>
    <p-table
      [value]="elements"
      [columns]="columns"
      [rows]="10"
      [paginator]="elements?.length > 10"
      [dataKey]="dataKey"
      [selectionMode]="selectionMode"
      [selection]="selected"
      (selectionChange)="handleSelection($event)"
    >
      <!-- header -->
      <ng-template pTemplate="header" let-columns>
        <tr>
          <th *ngIf="selectionMode === 'single'; else multiSelectAll" style="width: 4rem"></th>
          <ng-template #multiSelectAll>
            <th style="width: 4rem">
              <p-tableHeaderCheckbox></p-tableHeaderCheckbox>
            </th>
          </ng-template>

          <th *ngFor="let col of columns">{{ col.header }}</th>
        </tr>
      </ng-template>

      <!-- body -->
      <ng-template pTemplate="body" let-rowData let-columns="columns">
        <tr>
          <td *ngIf="selectionMode === 'single'; else multiSelect">
            <p-tableRadioButton [value]="rowData"></p-tableRadioButton>
          </td>
          <ng-template #multiSelect>
            <td>
              <p-tableCheckbox [value]="rowData"></p-tableCheckbox>
            </td>
          </ng-template>

          <td *ngFor="let col of columns">{{ rowData | dataCellFormatter: col }}</td>
        </tr>
      </ng-template>

      <ng-template pTemplate="emptymessage" let-columns>
        <tr>
          <td [attr.colspan]="columns?.length + 1">
            {{ 'recordsSearch.noData' | xTranslate }}
          </td>
        </tr>
      </ng-template>
    </p-table>`,
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class SelectionTableComponent {
  @Input() columns: Pick<Column, 'field' | 'header'>[];
  @Input() elements: Record<string, any>[];
  @Input() options: SelectionTableOptions = { selectMode: 'single', dataKey: 'id', tableTitle: '' };

  @Input() selected: Record<string, any>[] | Record<string, any> = null;
  @Output() selectedChange: EventEmitter<Record<string, any>[] | Record<string, any>> = new EventEmitter();

  get selectionMode() {
    return this.options.selectMode;
  }

  get dataKey(): string {
    return this.options.dataKey;
  }

  get title(): string {
    return this.options.tableTitle ?? null;
  }

  get noun(): string {
    return this.options.noun ?? null;
  }

  handleSelection(selection: Record<string, any>) {
    this.selected = selection;
    this.selectedChange.emit(selection);
  }
}
